import { Input } from "@light-technology/components";
import { Controller, useForm } from "react-hook-form";
import { api } from "../../state/api";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { FormCard } from "../shared/FormCard";
import { PasswordStrength } from "../shared/PasswordStrength";

type PasswordForm = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export const PasswordCard = () => {
  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isValid },
  } = useForm<PasswordForm>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onChange",
  });

  const changePasswordMutation = api.useChangePasswordMutation();
  const [changePassword, changePasswordState] = useMutationWithToast(
    changePasswordMutation,
    {
      successMessage: "Your password has been updated",
      errorMessage:
        "Your password could not be updated at this time. Please try again later.",
    },
  );

  const onPasswordSubmit = handleSubmit(({ currentPassword, newPassword }) => {
    changePassword({
      old_password: currentPassword,
      new_password: newPassword,
    }).then(() => {
      reset();
    });
  });

  return (
    <FormCard
      name="Password"
      subtext="Passwords must be more than 8 characters and not use common passwords."
      buttonText="Update Password"
      onButtonClick={onPasswordSubmit}
      isDisabled={!isDirty || !isValid}
      isLoading={changePasswordState.isLoading}
      data-ph-capture-attribute-action="update-password"
    >
      <div className="flex flex-col gap-2xl">
        <Controller
          control={control}
          name="currentPassword"
          rules={{
            required: "Current password is required",
            minLength: {
              value: 8,
              message: "Password must be at least 8 characters",
            },
          }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="password"
              label="Current Password"
              placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="newPassword"
          rules={{
            required: "New password is required",
            minLength: {
              value: 8,
              message: "Password must be at least 8 characters",
            },
            validate: (value, { currentPassword }) =>
              value !== currentPassword ||
              "New password must be different from current password",
          }}
          render={({ field, fieldState }) => (
            <div>
              <Input
                {...field}
                type="password"
                label="New Password"
                placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
                isRequired
                validationBehavior="aria"
                isInvalid={fieldState.invalid}
                hint={fieldState.error?.message}
              />
              <PasswordStrength password={field.value} />
            </div>
          )}
        />
        <Controller
          control={control}
          name="confirmPassword"
          rules={{
            required: "Please confirm your password",
            validate: (value, { newPassword }) =>
              value === newPassword || "Passwords do not match",
          }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="password"
              label="Confirm Password"
              placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
      </div>
    </FormCard>
  );
};
